import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { fetchTenant, tenantQueryKey } from './gateway.ts'
import { formatInstantWithoutZone, ownerTimeZone } from './instants.ts'
import { TenantAddress } from './tenant-address.tsx'

/**
 * The header every page of one tenant opens with — P05, and the amendment to
 * docs/ux/PLATFORM-ADMIN.md §3 and §5.1 that comes with it.
 *
 * A tenant has four pages (Overview, Settings, Branding, Feature flags), and until this
 * component each was a dead end that linked back to the one before it. Now they share:
 *
 * - **where you are** — a link back to the list, the tenant's name as the page's `<h1>`,
 *   and its state as a badge;
 * - **what it is** — one meta line: the slug (monospace, because it is copied), where the
 *   tenant is published (`TenantAddress`, which names this estate's hostname and no other),
 *   and when it was created in the **owner's** zone with that zone named (§7.5);
 * - **where else to go** — a `<nav aria-label="Tenant">` of four ordinary links.
 *
 * **The current tab is the router's call, not a prop.** Each tab is a `Link` with an exact
 * match, so `aria-current="page"` is on exactly the tab whose URL is the one on screen — a
 * page cannot be told it is "Settings" while the address says Branding. Exact matters for
 * Overview, whose path is the prefix of the other three.
 *
 * **It reads the tenant itself**, through the query key every tenant page already uses, so
 * it costs no extra request and cannot disagree with the page beneath it. It renders while
 * that read is in flight (the name reads "Tenant", the meta line is a placeholder, the tabs
 * work) and when the read failed in a way that is not a 404, so the tabs stay usable beside
 * the failure notice. It is **never rendered for a missing tenant** — the screens return
 * `NotFoundScreen` first, which is the only way the page can avoid revealing which ids
 * exist (§18.3).
 */
export function TenantHeader({ tenantId }: { readonly tenantId: string }) {
  const zone = ownerTimeZone()
  const tenant = useQuery({
    queryKey: tenantQueryKey(tenantId),
    queryFn: () => fetchTenant(tenantId),
  })
  const row = tenant.data

  // The strip scrolls inside itself when the four tabs do not fit (a 320 px phone), and it starts at
  // its left edge: a current tab beyond the right edge (Feature flags) was cut off and out of sight
  // (P08 mobile QA, D3). On mounting, the strip is moved — the strip only, never the page — until
  // the current tab is inside it. It jumps rather than glides, so there is no motion to switch off
  // for somebody who asked for less of it.
  const strip = useRef<HTMLElement>(null)
  useEffect(() => {
    const nav = strip.current
    const current = nav?.querySelector<HTMLElement>('[aria-current="page"]')
    if (nav == null || current == null) {
      return
    }
    const box = nav.getBoundingClientRect()
    const tab = current.getBoundingClientRect()
    if (tab.right > box.right) {
      nav.scrollLeft += tab.right - box.right
    } else if (tab.left < box.left) {
      nav.scrollLeft -= box.left - tab.left
    }
  }, [])

  return (
    <div className="tenant-head">
      <div>
        {/*
          The label names where it goes, because "Tenants" alone is also the masthead's link
          and two links with one name are indistinguishable in a list of links. It contains
          the visible word, so a voice-control user saying "click Tenants" still lands on it.
        */}
        <Link
          to="/tenants"
          className="crumb"
          aria-label="Back to Tenants"
          // Exact: the tenant's own address is under /tenants, and a link back to the list is
          // not the page the reader is on.
          activeOptions={{ exact: true }}
        >
          <span aria-hidden="true">←</span> Tenants
        </Link>
        <div className="tenant-title">
          <h1>{row?.name ?? 'Tenant'}</h1>
          {row === undefined ? null : (
            <span className={`badge badge-${row.state}`}>
              {row.state === 'retired' ? 'Retired' : 'Live'}
            </span>
          )}
        </div>
      </div>

      {row === undefined ? (
        <span className="skeleton skeleton-narrow" aria-hidden="true" />
      ) : (
        <p className="tenant-meta">
          <span>
            <code>{row.slug}</code>
          </span>
          <span>
            <TenantAddress slug={row.slug} />
          </span>
          <span>
            created {formatInstantWithoutZone(row.createdAt, zone)} <code>{zone}</code>
          </span>
        </p>
      )}

      <nav className="tabs" aria-label="Tenant" ref={strip}>
        <Link to="/tenants/$tenantId" params={{ tenantId }} activeOptions={{ exact: true }}>
          Overview
        </Link>
        <Link to="/tenants/$tenantId/settings" params={{ tenantId }}>
          Settings
        </Link>
        <Link to="/tenants/$tenantId/branding" params={{ tenantId }}>
          Branding
        </Link>
        <Link to="/tenants/$tenantId/flags" params={{ tenantId }}>
          Feature flags
        </Link>
      </nav>
    </div>
  )
}
